import type { OverlayPanelLayer } from "@frontend/pages/case-workspace/pathology/components/overlay-panel"
import { labelRasterStateToDiff } from "@frontend/pages/case-workspace/pathology/lib/label-raster-manifests"
import { overlayDisplayName } from "@frontend/pages/case-workspace/pathology/lib/source-display-name"
import type {
  LabelRasterSchema,
  LabelRasterTool,
  ParsedModuleSchema,
  ProjectModuleItem,
  WsiSource
} from "@frontend/pages/case-workspace/pathology/types"
import { useAnnotationStore } from "@frontend/stores/annotation-store"
import { runsToBytes } from "@shared/extensions/label-raster/manifest"
import { isLabelRasterClassSelectable, resolveLabelRasterEditRules } from "@shared/extensions/label-raster/schema"
import type { LabelRasterAnnotationState } from "@shared/lib/history-types"
import type {
  LabelRasterChangeEvent,
  LabelRasterDiff,
  LabelRasterErrorEvent,
  LabelRasterFillEvent,
  LabelRasterPickEvent,
  LabelRasterSourceInfo,
  LabelRasterStats,
  LabelRasterToolChangeEvent,
  TileViewer
} from "@virdx/wsi-viewer"
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"

interface UseLabelRasterEditingParams {
  viewerRef: RefObject<TileViewer | null>
  viewerEpoch: number
  activeModule: ProjectModuleItem | null
  activeModuleSchema: ParsedModuleSchema
  activeSeriesId: string | null
  wsiSources: WsiSource[] | undefined
  /** Overlay layers currently attached to the viewer (from useWsiOverlays). */
  overlayLayers: OverlayPanelLayer[]
  setOverlayLabelOpacity: (id: string, label: number, opacity: number) => void
  isActiveModuleEditable: boolean
  tool: LabelRasterTool | null
  onToolChange: (tool: LabelRasterTool | null) => void
  classId: number | null
  onClassChange: (classId: number) => void
  radiusPx: number
  onRadiusChange: (radiusPx: number) => void
  maxRadiusPx: number
  /** Overwrite mode (session / UI setting, never persisted). Off by default. */
  overwrite: boolean
  onOverwriteChange: (enabled: boolean) => void
  /**
   * The edit regions confining this module are not known yet (request pending /
   * failed, see `useEditRegions`): editing stays disabled rather than falling
   * open. The regions themselves are pushed to the viewer by `useEditRegions`.
   */
  editRegionsBlocked: boolean
  /** The edit-bbox request failed; editing is disabled and the panel shows why. */
  editBboxesError?: boolean
}

interface LabelRasterConfigOverrides {
  editable_class_ids?: number[]
  unannotated_id?: number
  brush_overwritable_ids?: number[]
  default_brush_radius_px?: number
}

function parseOverrides(config: string | null): LabelRasterConfigOverrides {
  if (!config) return {}
  try {
    const parsed = JSON.parse(config) as LabelRasterConfigOverrides
    return parsed && typeof parsed === "object" ? parsed : {}
  } catch {
    return {}
  }
}

export function labelRasterStateId(moduleId: string, overlaySeriesId: string): string {
  return `${moduleId}:${overlaySeriesId}`
}

function diffToState(params: {
  diff: LabelRasterDiff
  moduleId: string
  overlaySeriesId: string
  baseSeriesId: string | null
  domain: string
  locator?: string
}): LabelRasterAnnotationState {
  const { diff } = params
  return {
    id: labelRasterStateId(params.moduleId, params.overlaySeriesId),
    seriesId: params.overlaySeriesId,
    baseSeriesId: params.baseSeriesId,
    projectModuleId: params.moduleId,
    labelName: params.domain,
    labelValue: null,
    source: {
      arrayPath: diff.source.arrayPath,
      shape: [diff.source.shape[0], diff.source.shape[1]],
      chunkShape: [diff.source.chunkShape[0], diff.source.chunkShape[1]],
      dtype: diff.source.dtype,
      locator: params.locator ?? diff.source.locator,
      taxonomy: diff.source.taxonomy,
      pipelineVersion: diff.source.pipelineVersion
    },
    chunks: diff.chunks.map((c) => ({
      index: [c.index[0], c.index[1]],
      originalCrc32: c.originalCrc32,
      runs: runsToBytes(c.runs),
      changedPixels: c.changedPixels,
      bbox: [c.bbox[0], c.bbox[1], c.bbox[2], c.bbox[3]]
    })),
    classesUsed: [...diff.classesUsed],
    editedAt: diff.editedAt
  }
}

/**
 * Wires a `label_raster` module to the viewer's in-place label editing:
 * resolves the target overlay by domain, enables an edit session, maps the
 * panel tool state to the viewer, and keeps the annotation store (sparse
 * chunk runs) and the viewer in sync in both directions.
 */
export function useLabelRasterEditing({
  viewerRef,
  viewerEpoch,
  activeModule,
  activeModuleSchema,
  activeSeriesId,
  wsiSources,
  overlayLayers,
  setOverlayLabelOpacity,
  isActiveModuleEditable,
  tool,
  onToolChange,
  classId,
  onClassChange,
  radiusPx,
  onRadiusChange,
  maxRadiusPx,
  overwrite,
  onOverwriteChange,
  editRegionsBlocked,
  editBboxesError = false
}: UseLabelRasterEditingParams) {
  const labelRasters = useAnnotationStore((s) => s.labelRasters)
  const upsertLabelRaster = useAnnotationStore((s) => s.upsertLabelRaster)
  const removeLabelRaster = useAnnotationStore((s) => s.removeLabelRaster)

  const [sourceInfo, setSourceInfo] = useState<LabelRasterSourceInfo | null>(null)
  const [stats, setStats] = useState<LabelRasterStats | null>(null)
  const [crcMismatchCount, setCrcMismatchCount] = useState(0)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const isSyncingFromViewerRef = useRef(false)
  const enabledSessionsRef = useRef<Set<string>>(new Set())

  const schema: LabelRasterSchema | null =
    activeModule?.template_type === "label_raster" && activeModuleSchema?.type === "label_raster"
      ? activeModuleSchema
      : null
  const overrides = useMemo(() => parseOverrides(activeModule?.config ?? null), [activeModule?.config])
  // Stable identities: these feed effect dependency arrays, and a fresh array per
  // render would re-run the tool effect (and a viewer render) on every React render.
  // Eraser value + brush overwritable set (template schema, then per-project overrides).
  const editRulesKey = JSON.stringify(resolveLabelRasterEditRules(schema, overrides))
  const { unannotatedId, overwritableIds } = useMemo(
    () => JSON.parse(editRulesKey) as ReturnType<typeof resolveLabelRasterEditRules>,
    [editRulesKey]
  )
  const editableKey = JSON.stringify(overrides.editable_class_ids ?? schema?.editable_class_ids ?? null)
  const editableClassIds = useMemo(() => JSON.parse(editableKey) as number[] | null, [editableKey])
  const defaultRadius = overrides.default_brush_radius_px ?? schema?.default_brush_radius_px ?? 24

  const targetOverlay = useMemo(() => {
    if (!(schema && activeSeriesId)) return null
    return (
      (wsiSources ?? []).find(
        (s) => s.role === "overlay" && s.overlayOf === activeSeriesId && s.domain === schema.domain
      ) ?? null
    )
  }, [schema, activeSeriesId, wsiSources])
  const targetLayer = useMemo(
    () => (targetOverlay ? (overlayLayers.find((l) => l.id === targetOverlay.id) ?? null) : null),
    [overlayLayers, targetOverlay]
  )
  const attachedOverlayIds = useMemo(() => overlayLayers.map((l) => l.id).join("|"), [overlayLayers])
  // Fail closed: no tool until the server has said whether this slide has boxes.
  const regionsKnown = !editRegionsBlocked

  // Reset per-viewer state when the viewer is recreated.
  useEffect(() => {
    enabledSessionsRef.current.clear()
    setSourceInfo(null)
    setStats(null)
    setCrcMismatchCount(0)
    setErrorMessage(null)
  }, [viewerEpoch])

  // Per module: default brush radius, and overwrite mode back to off (it is destructive
  // and must never silently carry over into another module).
  const lastModuleIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!(schema && activeModule)) return
    if (lastModuleIdRef.current === activeModule.id) return
    lastModuleIdRef.current = activeModule.id
    onRadiusChange(defaultRadius)
    onOverwriteChange(false)
  }, [schema, activeModule, defaultRadius, onRadiusChange, onOverwriteChange])

  const ensureSession = useCallback(
    async (overlayId: string): Promise<LabelRasterSourceInfo | null> => {
      const viewer = viewerRef.current
      if (!viewer) return null
      if (!viewer.getOverlays().some((o) => o.id === overlayId)) return null
      try {
        const info = await viewer.enableLabelRasterEditing(overlayId)
        enabledSessionsRef.current.add(overlayId)
        return info
      } catch (err) {
        const message = err instanceof Error ? err.message : "Cannot edit this overlay"
        setErrorMessage(message)
        return null
      }
    },
    [viewerRef]
  )

  // Enable the edit session for the active module's target overlay. Which
  // overlay is visible is owned by `useWsiOverlays` (it follows the active
  // module). Depends on `attachedOverlayIds` (not the layer mirror) so the
  // effect retries once the overlay is attached but does not re-run on every
  // visibility / opacity change.
  useEffect(() => {
    const viewer = viewerRef.current
    if (!(viewer && viewerEpoch > 0 && schema && activeModule && targetOverlay)) return
    if (!attachedOverlayIds.split("|").includes(targetOverlay.id)) return
    const overlayId = targetOverlay.id
    let cancelled = false
    void (async () => {
      const info = await ensureSession(overlayId)
      if (cancelled || !info) return
      setSourceInfo(info)
      setErrorMessage(null)
      setStats(viewer.getLabelRasterStats(overlayId))
    })()
    return () => {
      cancelled = true
    }
  }, [viewerEpoch, schema, activeModule, targetOverlay, attachedOverlayIds, ensureSession, viewerRef])

  // Tool state → viewer.
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return
    viewer.setLabelRasterMaxBrushRadiusPx(Math.max(1, maxRadiusPx))
    if (!(schema && targetOverlay && sourceInfo && isActiveModuleEditable && regionsKnown && tool && classId != null)) {
      viewer.setLabelRasterTool(null)
      return
    }
    viewer.setLabelRasterTool({
      overlayId: targetOverlay.id,
      tool,
      classId,
      radiusPx: Math.max(1, Math.round(radiusPx)),
      unannotatedId,
      overwritableIds,
      overwrite
    })
  }, [
    viewerEpoch,
    schema,
    targetOverlay,
    sourceInfo,
    isActiveModuleEditable,
    regionsKnown,
    tool,
    classId,
    radiusPx,
    maxRadiusPx,
    unannotatedId,
    overwritableIds,
    overwrite,
    viewerRef
  ])

  // Clear the tool (and overwrite mode) when leaving the module / read-only.
  useEffect(() => {
    if (schema && isActiveModuleEditable) return
    if (tool !== null) onToolChange(null)
    if (overwrite) onOverwriteChange(false)
  }, [schema, isActiveModuleEditable, tool, onToolChange, overwrite, onOverwriteChange])

  // Viewer → store (strokes, resets) + pick/tool/error events.
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return

    const onChange = (event: LabelRasterChangeEvent) => {
      if (event.reason === "apply") return
      const moduleId = activeModule?.id
      const domain = schema?.domain
      if (!(moduleId && domain) || event.overlayId !== targetOverlay?.id) return
      const diff = viewer.getLabelRasterDiff(event.overlayId)
      const id = labelRasterStateId(moduleId, event.overlayId)
      isSyncingFromViewerRef.current = true
      if (diff && diff.chunks.length > 0) {
        upsertLabelRaster(
          diffToState({
            diff,
            moduleId,
            overlaySeriesId: event.overlayId,
            baseSeriesId: activeSeriesId,
            domain
          })
        )
      } else {
        removeLabelRaster(id)
      }
      setStats(viewer.getLabelRasterStats(event.overlayId))
    }
    const onPick = (event: LabelRasterPickEvent) => {
      if (event.overlayId !== targetOverlay?.id) return
      // "Unannotated" is what the eraser writes, never a paintable class; background (0) always is.
      if (event.value === unannotatedId) return
      if (!isLabelRasterClassSelectable(event.value, { unannotatedId, editableClassIds })) {
        toast.info(`Class ${event.name ?? event.value} is not editable in this module`)
        return
      }
      onClassChange(event.value)
    }
    const onToolEvent = (event: LabelRasterToolChangeEvent) => {
      const next = event.state?.tool ?? null
      if (next !== tool) onToolChange(next)
      if (event.state && Math.round(event.state.radiusPx) !== Math.round(radiusPx)) {
        onRadiusChange(Math.round(event.state.radiusPx))
      }
      // `O` inside the canvas toggles overwrite in the viewer; mirror it into the panel.
      if (event.state && (event.state.overwrite ?? false) !== overwrite) {
        onOverwriteChange(event.state.overwrite ?? false)
      }
    }
    const onError = (event: LabelRasterErrorEvent) => {
      setErrorMessage(event.message)
      toast.error("Label editing error", { description: event.message })
    }
    const onFill = (event: LabelRasterFillEvent) => {
      if (event.overlayId !== targetOverlay?.id) return
      if (event.status === "too-large") {
        // Only reachable when the deployment configures `labelRaster.maxFillPixels`; there is no default cap.
        toast.warning("Fill region too large", {
          description: `The connected region exceeds this deployment's limit of ${event.maxPixels.toLocaleString()} pixels and was not filled.`
        })
      } else if (event.status === "filled" && event.clipped) {
        // Fills only enter the label chunks on screen; a fixed id replaces the previous hint instead of stacking.
        toast.info("Fill stopped at the edge of the view", {
          id: "label-raster-fill-clipped",
          description: "Only the visible area was filled. Zoom out or pan and click again to fill more of the region."
        })
      } else if (event.status === "noop") {
        if (tool === "erase_fill") {
          toast.info("Nothing to erase", {
            description: overwrite
              ? "Click a class to mark its connected region as unannotated; background and unannotated pixels carry no label."
              : "Click a pixel of the selected class to mark its connected region as unannotated. Turn on Overwrite to erase the region of any class."
          })
        } else {
          toast.info("Nothing to fill", {
            description: overwrite
              ? "The clicked pixel already holds the selected class."
              : "Click a background or unannotated pixel to fill its connected region with the selected class (with Background selected, an unannotated pixel). Turn on Overwrite to replace the region of another class."
          })
        }
      }
    }

    viewer.on("label-raster-change", onChange)
    viewer.on("label-raster-pick", onPick)
    viewer.on("label-raster-tool-change", onToolEvent)
    viewer.on("label-raster-error", onError)
    viewer.on("label-raster-fill", onFill)
    return () => {
      viewer.off("label-raster-change", onChange)
      viewer.off("label-raster-pick", onPick)
      viewer.off("label-raster-tool-change", onToolEvent)
      viewer.off("label-raster-error", onError)
      viewer.off("label-raster-fill", onFill)
    }
  }, [
    viewerEpoch,
    activeModule?.id,
    schema?.domain,
    targetOverlay?.id,
    activeSeriesId,
    editableClassIds,
    unannotatedId,
    tool,
    radiusPx,
    overwrite,
    onClassChange,
    onToolChange,
    onRadiusChange,
    onOverwriteChange,
    upsertLabelRaster,
    removeLabelRaster,
    viewerRef
  ])

  // Store → viewer (undo/redo, session restore, module switches).
  useEffect(() => {
    if (isSyncingFromViewerRef.current) {
      isSyncingFromViewerRef.current = false
      return
    }
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0 || !activeSeriesId) return
    const attached = new Set(attachedOverlayIds.split("|").filter(Boolean))
    if (attached.size === 0) return

    let cancelled = false
    void (async () => {
      let mismatches = 0
      const touched = new Set<string>()
      for (const entry of labelRasters) {
        if (entry.baseSeriesId !== activeSeriesId && !attached.has(entry.seriesId)) continue
        if (!attached.has(entry.seriesId)) continue
        touched.add(entry.seriesId)
        const info = await ensureSession(entry.seriesId)
        if (cancelled || !info) continue
        const result = await viewer.setLabelRasterDiff(
          entry.seriesId,
          labelRasterStateToDiff(entry) as unknown as LabelRasterDiff
        )
        if (cancelled) return
        mismatches += result.crcMismatches.length
      }
      // Overlays with an active session but no store entry → back to the original.
      for (const overlayId of enabledSessionsRef.current) {
        if (touched.has(overlayId) || !attached.has(overlayId)) continue
        if ((viewer.getLabelRasterStats(overlayId)?.chunks ?? 0) > 0) {
          await viewer.setLabelRasterDiff(overlayId, null)
          if (cancelled) return
        }
      }
      if (targetOverlay) setStats(viewer.getLabelRasterStats(targetOverlay.id))
      setCrcMismatchCount(mismatches)
      if (mismatches > 0) {
        toast.warning("Source mask changed", {
          description: `${mismatches} edited chunk${mismatches === 1 ? "" : "s"} no longer match the original zarr; edits were applied anyway.`
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [labelRasters, viewerEpoch, activeSeriesId, attachedOverlayIds, ensureSession, targetOverlay, viewerRef])

  const resetEdits = useCallback(() => {
    const viewer = viewerRef.current
    if (!(viewer && targetOverlay)) return
    void viewer.resetLabelRasterEdits(targetOverlay.id)
  }, [viewerRef, targetOverlay])

  const toggleClassHidden = useCallback(
    (id: number) => {
      if (!targetOverlay) return
      const current = targetLayer?.labelOpacities[id] ?? 1
      setOverlayLabelOpacity(targetOverlay.id, id, current <= 0 ? 1 : 0)
    },
    [targetOverlay, targetLayer, setOverlayLabelOpacity]
  )

  return {
    schema,
    targetOverlay: targetOverlay
      ? {
          id: targetOverlay.id,
          label: overlayDisplayName(targetOverlay),
          seriesIdentifier: targetOverlay.seriesIdentifier ?? targetOverlay.label
        }
      : null,
    sourceInfo,
    labels: targetLayer ? targetLayer.labels : null,
    unannotatedId,
    labelOpacities: targetLayer?.labelOpacities ?? {},
    overlayOpacity: targetLayer?.opacity ?? 0,
    stats,
    crcMismatchCount,
    errorMessage:
      errorMessage ??
      (schema && editBboxesError
        ? "Could not load the editing regions for this slide, so editing is disabled. Reload the page to try again."
        : null),
    resetEdits,
    toggleClassHidden
  }
}
